import { type FC, useMemo } from 'react';

import Stepper from 'components/Stepper';
import { StepOptions } from 'components/Stepper/types';
import TrackView from 'features/Analytics/TrackView';
import { isFreeRole } from 'features/Team/utils';

import { useMemberInviteState } from '../MemberInviteProvider';
import LegalBeneficiarySelector from './steps/LegalBeneficiarySelector';
import MemberInformation from './steps/MemberInformation';
import MemberRole from './steps/MemberRole';
import Review from './steps/Review';

const MemberInviteStepper: FC = () => {
  const { memberRole, selectedLegalBeneficiary } = useMemberInviteState();
  const skipLastStep = memberRole.role && isFreeRole(memberRole.role);
  const steps: StepOptions[] = useMemo(
    () => [
      {
        key: 'legal-beneficiary-selector',
        render: (props) => (
          <>
            <TrackView pageName="MemberInviteLegalBeneficiarySelector" />
            <LegalBeneficiarySelector {...props} />
          </>
        ),
      },
      ...(!selectedLegalBeneficiary
        ? [
            {
              key: 'member-information',
              render: (props) => (
                <>
                  <TrackView pageName="MemberInviteMemberInformation" />
                  <MemberInformation {...props} />
                </>
              ),
            } satisfies StepOptions,
          ]
        : []),
      {
        key: 'member-role',
        render: (props) => (
          <>
            <TrackView pageName="MemberInviteMemberRole" />
            <MemberRole {...props} />
          </>
        ),
      },
      ...(skipLastStep
        ? []
        : [
            {
              key: 'review',
              render: (props) => (
                <>
                  <TrackView pageName="MemberInviteReview" />
                  <Review {...props} />
                </>
              ),
            } satisfies StepOptions,
          ]),
    ],
    [skipLastStep, selectedLegalBeneficiary],
  );

  return <Stepper steps={steps} />;
};

export default MemberInviteStepper;
